import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../authen/AuthContext";
import { FaSignOutAlt, FaBars, FaChevronDown, FaChevronRight } from "react-icons/fa";
import { MENU_ITEMS } from "./menu_const";
import { Container, Nav, Button, Offcanvas, Form } from "react-bootstrap";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showSidebar, setShowSidebar] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(localStorage.getItem("selected_branch_id") || "1");

  const handleBranchChange = (e) => {
    const newBranchId = e.target.value;
    localStorage.setItem("selected_branch_id", newBranchId);
    setSelectedBranch(newBranchId);
    window.location.reload(); // Reload the app to re-fetch data for the new branch
  };

  useEffect(() => {
    if (selectedBranch === "ALL" && !location.pathname.startsWith('/admin/reports')) {
      const defaultBranch = user?.allowed_branches?.[0];
      const branchId = defaultBranch ? (defaultBranch.id || defaultBranch) : "1";
      localStorage.setItem("selected_branch_id", branchId);
      setSelectedBranch(branchId);
      window.location.reload();
    }
  }, [location.pathname, selectedBranch, user]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const hasAccess = (roles) => {
    if (!user) return false;
    if (user.role === "master") return true;
    return roles.includes(user.role);
  };

  if (!user) {
    return <div className="p-4 text-center">Please log in to access this page.</div>;
  }

  const NavItem = ({ item, collapsed }) => {
    // Check if any child is currently active
    const isChildActive = item.subMenus?.some(sub => location.pathname.startsWith(sub.path));
    
    // Auto-open if child is active
    const [isOpen, setIsOpen] = useState(isChildActive);
    
    useEffect(() => {
      if (isChildActive) setIsOpen(true);
    }, [isChildActive]);

    const activeStyle = {
      padding: "1rem 1.25rem",
      color: "#1e3c72",
      fontWeight: "600",
      background: "#f0f4f8",
      borderRight: "4px solid #1e3c72",
      borderRadius: "4px",
      marginBottom: "0.25rem",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      textDecoration: "none",
      justifyContent: collapsed ? "center" : "flex-start",
    };

    const inactiveStyle = {
      padding: "1rem 1.25rem",
      color: "#555",
      fontWeight: "500",
      background: "transparent",
      borderRight: "4px solid transparent", // keep border to prevent layout shift
      borderRadius: "4px",
      marginBottom: "0.25rem",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      textDecoration: "none",
      transition: "background 0.2s",
      justifyContent: collapsed ? "center" : "flex-start",
    };

    if (item.subMenus) {
      return (
        <div className="mb-2">
          <div
            onClick={() => {
              if (collapsed) setIsCollapsed(false);
              setIsOpen(!isOpen);
            }}
            style={{
              ...(isChildActive ? activeStyle : inactiveStyle),
              cursor: "pointer",
              justifyContent: "space-between" 
            }}
            onMouseOver={(e) => { if(!isChildActive) e.currentTarget.style.background = "#f0f4f8"; }}
            onMouseOut={(e) => { if(!isChildActive) e.currentTarget.style.background = "transparent"; }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px", justifyContent: collapsed ? "center" : "flex-start", width: collapsed ? "100%" : "auto" }}>
              <item.icon size={18} /> 
              {!collapsed && <span>{item.name}</span>}
            </div>
            {!collapsed && (
              <span style={{ fontSize: "0.8rem", color: "#888" }}>
                {isOpen ? <FaChevronDown /> : <FaChevronRight />}
              </span>
            )}
          </div>
          {isOpen && !collapsed && (
            <div style={{ paddingLeft: "1.5rem" }}>
              {item.subMenus.filter((sub) => hasAccess(sub.roles)).map((sub) => (
                <Nav.Link
                  as={NavLink}
                  to={sub.path}
                  key={sub.path}
                  onClick={() => setShowSidebar(false)}
                  style={({ isActive }) => (isActive ? activeStyle : inactiveStyle)}
                >
                  <sub.icon size={16} /> <span>{sub.name}</span>
                </Nav.Link>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="mb-2">
        <Nav.Link
          as={NavLink}
          to={item.path}
          onClick={() => setShowSidebar(false)}
          style={({ isActive }) => (isActive ? activeStyle : inactiveStyle)}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px", justifyContent: collapsed ? "center" : "flex-start", width: collapsed ? "100%" : "auto" }}>
            <item.icon size={18} /> {!collapsed && <span>{item.name}</span>}
          </div>
        </Nav.Link>
      </div>
    );
  };

  const SidebarContent = ({ collapsed }) => (
    <div className="d-flex flex-column h-100" style={{ background: "#ffffff" }}>
      <div 
        className="p-3 border-bottom d-flex align-items-center justify-content-center" 
        style={{ height: "70px", cursor: "pointer" }}
        onClick={() => setIsCollapsed(!collapsed)}
        title="Toggle Sidebar"
      >
        {!collapsed && <h4 style={{ color: "#1e3c72", fontWeight: 700, margin: 0, whiteSpace: "nowrap" }}>Minecaff OMS</h4>}
        {collapsed && <h4 style={{ color: "#1e3c72", fontWeight: 800, margin: 0 }}>M</h4>}
      </div>
      
      {/* Branch Selector */}
      {user?.allowed_branches && user.allowed_branches.length > 0 && !collapsed && (
        <div className="px-3 pt-3">
          <Form.Group>
            <Form.Label className="small text-muted mb-1 fw-bold">Select Branch</Form.Label>
            <Form.Select 
              size="sm" 
              value={selectedBranch} 
              onChange={handleBranchChange}
              style={{ borderColor: "#1e3c72", color: "#1e3c72", fontWeight: "600" }}
            >
              {user.allowed_branches.length > 1 && location.pathname.startsWith('/admin/reports') && (
                <option value="ALL">All Branches</option>
              )}
              {user.allowed_branches.map(b => {
                const bId = b.id || b;
                const bName = b.name || `Branch ${b}`;
                return <option key={bId} value={bId}>{bName}</option>;
              })}
            </Form.Select>
          </Form.Group>
        </div>
      )}

      <div className="flex-grow-1" style={{ overflowY: "auto", overflowX: "hidden", minHeight: 0 }}>
        <Nav className="flex-column p-2">
          {MENU_ITEMS.filter((item) => hasAccess(item.roles)).map((item, index) => (
            <NavItem item={item} key={item.path || index} collapsed={collapsed} />
          ))}
        </Nav>
      </div>
      <div className="p-3 border-top d-flex flex-column align-items-center">
        {!collapsed && (
          <div className="mb-3 text-muted small text-center w-100" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            Logged in as: <br /><strong>{user.display_name}</strong>
          </div>
        )}
        <Button 
          variant="outline-danger" 
          className="w-100 d-flex align-items-center justify-content-center gap-2" 
          onClick={handleLogout}
          style={{ padding: collapsed ? "0.5rem" : "0.5rem 1rem" }}
          title="Logout"
        >
          <FaSignOutAlt size={18} /> {!collapsed && <span>Logout</span>}
        </Button>
      </div>
    </div>
  );

  return (
    <Container fluid className="p-0" style={{ height: "100vh", overflow: "hidden", backgroundColor: "#f8f9fa", display: "flex", flexDirection: "column" }}>
      <div className="d-flex flex-grow-1 h-100" style={{ overflow: "hidden" }}>
        {/* Desktop Sidebar */}
        <div 
          className="d-none d-md-block border-end shadow-sm bg-white flex-shrink-0" 
          style={{ width: isCollapsed ? "80px" : "280px", transition: "width 0.3s ease-in-out", height: "100%" }}
        >
          <SidebarContent collapsed={isCollapsed} />
        </div>

        {/* Main Content Area */}
        <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 0, height: "100%" }}>
          {/* Mobile Topbar */}
          <div className="d-md-none bg-white border-bottom px-3 py-2 shadow-sm d-flex align-items-center">
            <Button variant="light" onClick={() => setShowSidebar(true)}>
              <FaBars />
            </Button>
            <span className="ms-3 text-muted fw-bold">Menu</span>
          </div>

          {/* Page Content */}
          <div className="flex-grow-1 p-4" style={{ overflowY: "auto", height: "100%" }}>
            <Outlet />
          </div>
        </div>
      </div>

      {/* Mobile Sidebar (Offcanvas) */}
      <Offcanvas show={showSidebar} onHide={() => setShowSidebar(false)} placement="start" style={{ width: "280px" }}>
        <Offcanvas.Body className="p-0">
          <SidebarContent collapsed={false} />
        </Offcanvas.Body>
      </Offcanvas>
    </Container>
  );
}
